// TermsConditions.js
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Box,Typography,TextField,IconButton,List,ListItem,ListItemText,Stack,Button,Tooltip,Dialog,DialogTitle,DialogContent,DialogActions,CircularProgress} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MUITypography from "../../../Components/MUITypography";
import {
  API_ADD_TERMS_CONDITIONS,
  API_DELETE_TERMS_CONDITIONS,
  API_GET_TERMS_CONDITIONS_LIST,
  API_UPDATE_TERMS_CONDITIONS,
} from "../../../Util/URLConstants";
import StorageFactory from "../../../Util/StorageFactory";
import { SESSION_USER_ID_KEY } from "../../../Util/VMConstants";
import { align_center, align_column, VM_Colors } from "../../../Styles/VMStyles";

export default function TermsConditions({ terms, setTerms }) {
  const [newTerm, setNewTerm] = useState("");
  const [addError, setAddError] = useState("");
  const [loading, setLoading] = useState(false);

  // editing uses id (if available) or index fallback
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editError, setEditError] = useState("");
  const [editingBusyId, setEditingBusyId] = useState(null);

  const [deleteIdOrIndex, setDeleteIdOrIndex] = useState(null); // { id, index } or { index }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingBusyIdOrIndex, setDeletingBusyIdOrIndex] = useState(null);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  // Normalize terms for rendering to array of objects: { id: <maybe null>, term: string }
  const normalizedItems = Array.isArray(terms)
    ? terms.map((t) =>
        typeof t === "string" ? { id: null, term: t } : { id: t.id ?? null, term: t.term ?? t.name ?? "" }
      )
    : [];

  // Helper: reload list from server and update parent via setTerms(payload.data)
  const loadFromServer = useCallback(async () => {
    setLoading(true);
    setAddError("");
    try {
      const url = `${API_GET_TERMS_CONDITIONS_LIST}?user_id=${encodeURIComponent(userID)}`;
      const res = await axios.get(url, { headers: { "Content-Type": "application/json" } });
      const payload = res.data;
      if (payload && payload.status_code === 200 && Array.isArray(payload.data)) {
        // give parent the raw payload.data so caller receives same shape server returned
        setTerms(payload.data);
      } else {
        const serverMessage =
          (payload && (payload.message || payload.data)) || `Failed to load terms (status ${res.status})`;
        setAddError(serverMessage);
      }
    } catch (err) {
      setAddError(err?.response?.data?.message || err?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }, [userID, setTerms]);

  // Load on mount
  useEffect(() => {
    loadFromServer();
  }, [loadFromServer]);

  // Utility: check duplicate against normalizedItems
  const isDuplicate = (candidate, ignoreId = null, ignoreIndex = -1) => {
    const normalized = (candidate || "").trim().toLowerCase();
    return normalizedItems.some((item, i) => {
      if (ignoreId != null) {
        if (item.id === ignoreId) return false;
      } else if (i === ignoreIndex) {
        return false;
      }
      return String(item.term || "").trim().toLowerCase() === normalized;
    });
  };

  // ADD
  const handleAddTerm = async () => {
    const trimmed = (newTerm || "").trim();
    if (!trimmed) {
      setAddError("Please enter a term.");
      return;
    }
    if (isDuplicate(trimmed)) {
      setAddError("This term already exists.");
      return;
    }

    setAddError("");
    setLoading(true);
    try {
      const body = { term: trimmed, user_id: userID };
      const res = await axios.post(API_ADD_TERMS_CONDITIONS, body, {
        headers: { "Content-Type": "application/json" },
      });
      const payload = res.data;
      if (payload && (payload.status_code === 200 || res.status === 200)) {
        // reload authoritative list
        await loadFromServer();
        setNewTerm("");
      } else {
        const errMsg = (payload && (payload.message || payload.data)) || `Failed to add term (status ${res.status})`;
        setAddError(errMsg);
      }
    } catch (err) {
      setAddError(err?.response?.data?.message || err?.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  // const handleAddKeyDown = (e) => {
  //   if (e.key === "Enter") {
  //     e.preventDefault();
  //     handleAddTerm();
  //   }
  // };

  // EDIT
  const startEdit = (item, index) => {
    setEditingId(item.id ?? index); // use id when present, else use index as fallback
    setEditingText(item.term);
    setEditError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
    setEditError("");
  };

  const saveEdit = async (itemOrId, indexFallback) => {
    // itemOrId might be an id (number/string) or index (number)
    // compute the target item from normalizedItems
    const targetIndex =
      typeof itemOrId === "number" && normalizedItems.findIndex((it) => it.id === itemOrId) >= 0
        ? normalizedItems.findIndex((it) => it.id === itemOrId)
        : typeof itemOrId === "number"
        ? indexFallback
        : -1;

    const original = normalizedItems[targetIndex] ? normalizedItems[targetIndex].term : "";
    const id = normalizedItems[targetIndex] ? normalizedItems[targetIndex].id : null;

    const trimmed = (editingText || "").trim();
    if (!trimmed) {
      setEditError("Term cannot be empty.");
      return;
    }

    // check duplicate: ignore this item's id/index
    if (id != null) {
      if (isDuplicate(trimmed, id, -1)) {
        setEditError("Another term with same text exists.");
        return;
      }
    } else {
      if (isDuplicate(trimmed, null, targetIndex)) {
        setEditError("Another term with same text exists.");
        return;
      }
    }

    setEditError("");
    setEditingBusyId(itemOrId);
    try {
      // If server provides ids, prefer sending { id, term }
      let res;
      if (id != null) {
        const body = { id, term: trimmed, user_id: userID };
        res = await axios.post(API_UPDATE_TERMS_CONDITIONS, body, {
          headers: { "Content-Type": "application/json" },
        });
      } else {
        // fallback to old/new approach if no id available
        const body = { old_term: original, new_term: trimmed, user_id: userID };
        res = await axios.post(API_UPDATE_TERMS_CONDITIONS, body, {
          headers: { "Content-Type": "application/json" },
        });
      }

      const payload = res.data;
      if (payload && (payload.status_code === 200 || res.status === 200)) {
        await loadFromServer();
        cancelEdit();
      } else {
        const errMsg = (payload && (payload.message || payload.data)) || `Failed to update term (status ${res.status})`;
        setEditError(errMsg);
      }
    } catch (err) {
      setEditError(err?.response?.data?.message || err?.message || "Network error");
    } finally {
      setEditingBusyId(null);
    }
  };

  // DELETE
  const handleDeleteClick = (item, index) => {
    // store object { id, index, term }
    setDeleteIdOrIndex({ id: item.id ?? null, index, term: item.term });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteIdOrIndex) {
      setConfirmOpen(false);
      return;
    }

    setDeletingBusyIdOrIndex(deleteIdOrIndex.id ?? deleteIdOrIndex.index);

    try {
      if (deleteIdOrIndex.id != null) {
        // call delete endpoint by id (preferred)
        // try using DELETE with params
        const res = await axios.get(API_DELETE_TERMS_CONDITIONS, {
          params: { id: deleteIdOrIndex.id, user_id: userID },
          headers: { "Content-Type": "application/json" },
        });
        const payload = res.data;
        if (!(payload && (payload.status_code === 200 || res.status === 200))) {
          throw new Error((payload && (payload.message || payload.data)) || `Failed to delete (status ${res.status})`);
        }
      } else {
        // fallback: delete by term string as query param
        const url = `${API_DELETE_TERMS_CONDITIONS}?term=${encodeURIComponent(deleteIdOrIndex.term)}&user_id=${encodeURIComponent(
          userID
        )}`;
        const res = await axios.get(url, { headers: { "Content-Type": "application/json" } });
        const payload = res.data;
        if (!(payload && (payload.status_code === 200 || res.status === 200))) {
          throw new Error((payload && (payload.message || payload.data)) || `Failed to delete (status ${res.status})`);
        }
      }

      // refresh
      await loadFromServer();
      setConfirmOpen(false);
      setDeleteIdOrIndex(null);
      // reset editing if needed
      if (editingId === (deleteIdOrIndex.id ?? deleteIdOrIndex.index)) cancelEdit();
    } catch (err) {
      setAddError(err?.response?.data?.message || err?.message || "Network error");
      setConfirmOpen(false);
    } finally {
      setDeletingBusyIdOrIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIdOrIndex(null);
  };

  const renderActionButtonsForIndex = (item, index) => (
    <Stack direction="row" spacing={0.5}>
      <Tooltip title="Edit">
        <span>
          <IconButton
            size="small"
            onClick={() => startEdit(item, index)}
            aria-label={`edit-${index}`}
            disabled={loading || editingBusyId != null || deletingBusyIdOrIndex != null}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete">
        <span>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(item, index)}
            aria-label={`delete-${index}`}
            disabled={loading || editingBusyId != null || deletingBusyIdOrIndex != null}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Box>
      <Box>
        <MUITypography variant="h3" gutterBottom sx={{ mt: 2 ,fontWeight: 700}}>
          Terms & Conditions
        </MUITypography>
      </Box>

      <Box
        sx={{
          mt: 1,
          mb: 3,
          p: 2,
          border: "1px solid #ddd",
          backgroundColor: VM_Colors.white,
          borderRadius: 2,
          ...align_column,
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading terms...</Typography>
          </Box>
        )}

        {normalizedItems.length === 0 ? (
          <Typography sx={{ mb: 1, color: "text.secondary" }}>No terms added yet.</Typography>
        ) : (
          <List dense sx={{ width: "100%" }}>
            {normalizedItems.map((item, idx) => {
              const key = item.id != null ? `t-${item.id}` : `t-${idx}-${item.term}`;
              const isEditing = (item.id != null ? editingId === item.id : editingId === idx);
              return (
                <ListItem key={key} divider sx={{ alignItems: "flex-start" }}>
                  {isEditing ? (
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        value={editingText}
                        onChange={(e) => {
                          setEditingText(e.target.value);
                          if (editError) setEditError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveEdit(item.id != null ? item.id : idx);
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelEdit();
                          }
                        }}
                        size="small"
                        fullWidth
                        autoFocus
                        helperText={editError || ""}
                        error={!!editError}
                        inputProps={{ "aria-label": `edit-term-${idx}` }}
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={editingBusyId === (item.id ?? idx) ? <CircularProgress size={16} /> : <CheckIcon />}
                          onClick={() => saveEdit(item.id != null ? item.id : idx)}
                          aria-label={`save-term-${idx}`}
                          disabled={editingBusyId === (item.id ?? idx)}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CloseIcon />}
                          onClick={cancelEdit}
                          aria-label={`cancel-edit-${idx}`}
                          disabled={editingBusyId === (item.id ?? idx)}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <>
                      <ListItemText primary={`• ${item.term}`} sx={{ whiteSpace: "pre-wrap" }} />
                      {renderActionButtonsForIndex(item, idx)}
                    </>
                  )}
                </ListItem>
              );
            })}
          </List>
        )}

        <Stack direction="row" spacing={1} sx={{ width: "100%", mt: 1 }} alignItems="center">
          <TextField
            label="Add Terms & Conditions"
            value={newTerm}
            onChange={(e) => {
              setNewTerm(e.target.value);
              if (addError) setAddError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddTerm();
              }
            }}
            fullWidth
            size="small"
            helperText={addError || ""}
            error={!!addError}
            inputProps={{ "aria-label": "add-term-input" }}
            disabled={loading}
          />
          <Button
            variant="contained"
            size="medium"
            onClick={handleAddTerm}
            aria-label="add-term-button"
            startIcon={loading ? <CircularProgress size={18} /> : <AddIcon />}
            disabled={loading}
          >
            Add
          </Button>
        </Stack>
      </Box>

      <Dialog open={confirmOpen} onClose={handleCancelDelete} aria-labelledby="confirm-delete-dialog">
        <DialogTitle id="confirm-delete-dialog">
            <MUITypography variant={'h3'} sx={align_center}>Delete Term</MUITypography>
        </DialogTitle>
        <DialogContent>
          <Typography mt={4}>Are you sure you want to delete this term?</Typography>
          {deleteIdOrIndex && (
            <Box mt={1} sx={{ fontStyle: "italic", color: "text.secondary" }}>
              {deleteIdOrIndex.term}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            startIcon={deletingBusyIdOrIndex != null ? <CircularProgress size={16} /> : <DeleteIcon />}
            disabled={deletingBusyIdOrIndex != null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

TermsConditions.propTypes = {
  // `terms` may be array of strings or array of { id, term }
  terms: PropTypes.array,
  setTerms: PropTypes.func.isRequired,
};
